import { useState, type FormEvent } from 'react';
import { controleerIban } from '@qc/core';
import { Knop } from '@qc/ui';
import { supabase } from '../supabase.js';
import { Veld } from './Veld.js';

export interface AanmeldschermProps {
  gebruikerId: string;
  email: string;
  onKlaar: () => Promise<void>;
  onUitloggen: () => Promise<void>;
}

/**
 * De bedrijfsgegevens van de organisator. Dit is de tweede en laatste stap van
 * de zelfaanmelding: daarna kan hij meteen een evenement aanmaken.
 *
 * Het IBAN wordt hier gecontroleerd. Een typefout komt er anders pas uit als
 * het geld niet aankomt, en dan is het evenement al voorbij.
 */
export function Aanmeldscherm({ gebruikerId, email, onKlaar, onUitloggen }: AanmeldschermProps) {
  const [bedrijfsnaam, setBedrijfsnaam] = useState('');
  const [kvk, setKvk] = useState('');
  const [btw, setBtw] = useState('');
  const [iban, setIban] = useState('');
  const [contactpersoon, setContactpersoon] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  // Blijft staan zodra de organisator-rij is opgeslagen, ook als de koppeling
  // daarna mislukt — anders maakt een tweede poging een nieuwe, wees rij aan.
  const [organisatorId, setOrganisatorId] = useState<string | null>(null);

  const ibanControle = iban === '' ? null : controleerIban(iban);

  async function verzend(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    setFout(null);

    const controle = controleerIban(iban);
    if (!controle.geldig) {
      setFout(controle.melding);
      return;
    }

    setBezig(true);

    // Het id maken we hier, en we lezen het niet terug.
    //
    // Dat is geen omweg maar een noodzaak: een gebruiker zonder profiel mag nog
    // geen enkele organisator lezen, ook niet de rij die hij net zelf heeft
    // aangemaakt. Zou hij het id moeten ophalen, dan kwam hij nooit voorbij deze
    // stap. Zelf een UUID maken is bovendien wat de kassa ook doet (regel 3).
    //
    // Was een vorige poging al tot hier gekomen, dan bestaat de organisator-rij
    // al: opnieuw aanmaken zou een tweede, wees rij opleveren. Dan alleen het
    // account er alsnog aan koppelen.
    let id = organisatorId;
    if (id === null) {
      id = crypto.randomUUID();
      const { error: organisatorFout } = await supabase.from('organisatoren').insert({
        id,
        bedrijfsnaam,
        kvk_nummer: kvk === '' ? null : kvk,
        btw_nummer: btw === '' ? null : btw,
        iban: controle.genormaliseerd,
        contactpersoon: contactpersoon === '' ? null : contactpersoon,
        email,
        telefoon: telefoon === '' ? null : telefoon,
      });

      if (organisatorFout) {
        setBezig(false);
        setFout('Opslaan lukte niet. Probeer het zo nog eens.');
        return;
      }
      setOrganisatorId(id);
    }

    const { error: profielFout } = await supabase.from('profielen').insert({
      gebruiker_id: gebruikerId,
      rol: 'organizer',
      naam: contactpersoon === '' ? bedrijfsnaam : contactpersoon,
      organizer_id: id,
    });

    setBezig(false);

    if (profielFout) {
      setFout(
        'Je gegevens staan er, maar je account koppelen lukte niet. Probeer het zo nog eens.',
      );
      return;
    }

    await onKlaar();
  }

  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col justify-center gap-6 p-8">
      <header>
        <p className="text-tekst-zacht">Nog één stap</p>
        <h1 className="text-3xl font-bold">Je bedrijfsgegevens</h1>
        <p className="mt-2 text-tekst-zacht">
          Hier gaat je commissie naartoe en hierop staan je facturen.
        </p>
      </header>

      <form onSubmit={(e) => void verzend(e)} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Veld
            label="Bedrijfsnaam"
            required
            value={bedrijfsnaam}
            onChange={(e) => setBedrijfsnaam(e.target.value)}
          />
        </div>
        <Veld label="KvK-nummer" value={kvk} onChange={(e) => setKvk(e.target.value)} />
        <Veld label="BTW-nummer" value={btw} onChange={(e) => setBtw(e.target.value)} />
        <div className="sm:col-span-2">
          <Veld
            label="IBAN"
            required
            value={iban}
            onChange={(e) => setIban(e.target.value)}
            fout={ibanControle !== null && !ibanControle.geldig}
            toelichting={
              ibanControle === null
                ? 'Hierop maken we je commissie over.'
                : ibanControle.geldig
                  ? 'Dit rekeningnummer klopt.'
                  : ibanControle.melding
            }
          />
        </div>
        <Veld
          label="Contactpersoon"
          value={contactpersoon}
          onChange={(e) => setContactpersoon(e.target.value)}
        />
        <Veld
          label="Telefoonnummer"
          type="tel"
          value={telefoon}
          onChange={(e) => setTelefoon(e.target.value)}
        />

        {fout !== null && <p className="text-lg text-fout sm:col-span-2">{fout}</p>}

        <div className="sm:col-span-2">
          <Knop nadruk="hoofd" type="submit" disabled={bezig} className="w-full">
            {bezig ? 'Bezig…' : 'Opslaan en verder'}
          </Knop>
        </div>
      </form>

      <Knop nadruk="stil" onClick={() => void onUitloggen()} className="text-base">
        Uitloggen
      </Knop>
    </div>
  );
}
